import { Alert, AlertTitle, Snackbar, SnackbarOrigin } from '@mui/material';
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type SnackbarType = 'success' | 'info' | 'warning' | 'error';

interface SnackbarOptions {
  anchorOrigin?: SnackbarOrigin;
  duration?: number | null; // Ensure null can be handled for indefinite duration
  variant?: 'filled' | 'outlined' | 'standard';
  action?: ReactNode;
}

interface SnackbarItem {
  id: number;
  title: string | ReactNode;
  message: string | ReactNode;
  type: SnackbarType;
  options: SnackbarOptions;
}

interface SnackbarContextType {
  showSnackbar: (title: string | ReactNode, message: string | ReactNode, type?: SnackbarType, options?: SnackbarOptions) => void;
  showManualSnackbar: (title: string | ReactNode, message: string | ReactNode, type?: SnackbarType, options?: SnackbarOptions) => { id: number; close: () => void };
  enableStackedMode: () => void;
  enableSequentialMode: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

interface SnackbarProviderProps {
  children?: ReactNode;
}

const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbarQueue, setSnackbarQueue] = useState<SnackbarItem[]>([]);
  const [visibleSnackbars, setVisibleSnackbars] = useState<SnackbarItem[]>([]);
  const [isStackedMode, setIsStackedMode] = useState(false);

  const handleSnackbarClose = useCallback((id: number) => {
    setVisibleSnackbars((prev) => prev.filter((snack) => snack.id !== id));
  }, []);

  const showSnackbar = useCallback((title: string | ReactNode, message: string | ReactNode, type: SnackbarType = 'info', options: SnackbarOptions = {}) => {
    const id = Date.now() + Math.random(); // Ensure unique ID
    const newSnackbar: SnackbarItem = {
      id,
      title,
      message,
      type,
      options: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        duration: 16000,
        ...options,
      },
    };
    setSnackbarQueue((prevQueue) => [...prevQueue, newSnackbar]);
  }, []);

  const showManualSnackbar = useCallback((title: string | ReactNode, message: string | ReactNode, type: SnackbarType = 'info', options: SnackbarOptions = {}) => {
    const id = Date.now() + Math.random(); // Ensure unique ID
    const newSnackbar: SnackbarItem = {
      id,
      title,
      message,
      type,
      options: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        duration: 4000,
        ...options,
      },
    };
    setSnackbarQueue((prevQueue) => [...prevQueue, newSnackbar]);

    const close = () => handleSnackbarClose(id);
    return { id, close };
  }, [handleSnackbarClose]);

  const enableStackedMode = useCallback(() => setIsStackedMode(true), []);
  const enableSequentialMode = useCallback(() => setIsStackedMode(false), []);

  useEffect(() => {
    if (snackbarQueue.length > 0) {
      if (isStackedMode) {
        setVisibleSnackbars((prev) => [...prev, ...snackbarQueue]);
      } else {
        const [firstSnackbar] = snackbarQueue;
        if (firstSnackbar) {
          setVisibleSnackbars([firstSnackbar]);
        }
      }
      setSnackbarQueue([]); // clear the queue after state update
    }
  }, [snackbarQueue, isStackedMode]);

  const calculateVerticalPosition = useMemo(() => {
    return (index: number, totalSnackbars: number, anchorOrigin?: SnackbarOrigin) => {
      const spacing = 60;
      const offset = 16;

      if (anchorOrigin?.vertical === 'top' && anchorOrigin?.horizontal === 'center') {
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed' as const
        };
      }

      return { bottom: `${(totalSnackbars - 1 - index) * spacing + offset}px` };
    };
  }, []); // Memoize position calculation

  const contextValue = useMemo(
    () => ({ showSnackbar, showManualSnackbar, enableStackedMode, enableSequentialMode }),
    [showSnackbar, showManualSnackbar, enableStackedMode, enableSequentialMode],
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      {visibleSnackbars.map((snackbar, index) => {
        const verticalPosition = calculateVerticalPosition(index, visibleSnackbars.length, snackbar.options.anchorOrigin);

        // Handle duration fallback if not defined or set to null
        const autoHideDuration = snackbar.options.duration === null ? null : (snackbar.options.duration ?? 3000);
        const isManualCloseOnly = snackbar.options.duration === null;

        return (
          <Snackbar
            key={snackbar.id}
            open={true}
            autoHideDuration={autoHideDuration}
            onClose={isManualCloseOnly ? undefined : () => handleSnackbarClose(snackbar.id)}
            anchorOrigin={snackbar.options.anchorOrigin}
            style={verticalPosition}
            aria-live="assertive" // Ensures accessibility for screen readers
          >
            <Alert
              onClose={isManualCloseOnly ? undefined : () => handleSnackbarClose(snackbar.id)}
              severity={snackbar.type}
              variant={snackbar.options.variant ?? 'standard'}
              action={snackbar.options.action}
            >
              <AlertTitle>
                {typeof snackbar.title === 'string' ? <strong>{snackbar.title}</strong> : snackbar.title}
              </AlertTitle>
              {snackbar.message}
            </Alert>
          </Snackbar>
        );
      })}
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
